import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Receipt, TrendingUp, TrendingDown, ChevronDown } from "lucide-react";

const fmt = (n) => `${n > 0 ? "+" : ""}${Number(n).toFixed(2)}`;

export function PsiReceiptCard({ ev, compact = false, testid = "psi-receipt" }) {
  const r = ev?.receipt;
  const delta = ev?.delta ?? r?.delta;
  if (delta == null) return null;
  const up = delta > 0;
  const tone = up ? "text-emerald-300" : "text-pb-red";
  return (
    <div className={`border ${up ? "border-emerald-500/40" : "border-pb-red/40"} bg-zinc-950 p-3`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest text-amber-300"><Receipt className="w-3 h-3" /> PSI movement receipt</div>
          <div className="text-xs text-zinc-300 truncate" data-testid={`${testid}-match`}>
            <span className={`font-black ${ev.won ? "text-emerald-300" : "text-pb-red"}`}>{ev.won ? "WIN" : "LOSS"}</span>
            {ev.opp ? ` vs ${ev.opp}` : ""}{ev.score ? ` · ${ev.score}` : ""}{ev.where ? ` · ${ev.where}` : ""}
          </div>
        </div>
        <div className={`flex items-center gap-1 font-mono font-black text-2xl leading-none ${tone}`} data-testid={`${testid}-delta`}>
          {up ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}{fmt(delta)}
        </div>
      </div>
      {!r && <div className="mt-1.5 text-[9px] text-zinc-600" data-testid={`${testid}-legacy`}>Scored before itemized receipts — newer matches show the full breakdown.</div>}
      {r && (
        <div className={`mt-2 grid ${compact ? "grid-cols-2" : "grid-cols-2 sm:grid-cols-4"} gap-1.5 text-[10px]`} data-testid={`${testid}-lines`}>
          <Line label="Expected win" value={`${r.expected_pct}%`} hint={`${r.doubles ? "team " : ""}${r.my_r} vs ${r.opp_r}`} testid={`${testid}-expected`} />
          <Line label={ev.won ? "Upset value" : "Result cost"} value={fmt(r.base)} hint={ev.won ? (r.expected_pct < 50 ? "underdog win" : "expected win") : (r.expected_pct > 50 ? "favorite loss" : "expected loss")} testid={`${testid}-base`} />
          <Line label="Margin kicker" value={fmt(r.kicker)} hint={`${r.margin_pct}% margin`} testid={`${testid}-kicker`} />
          <Line label={r.cap_hit ? "Cap hit" : r.floor_hit ? "Floor applied" : "Cap"} value={r.cap_hit ? `±${r.cap} max` : r.floor_hit ? "±0.01 min" : `±${r.cap}`}
            hint={r.cap_hit ? `raw ${fmt(r.raw)} → capped` : r.floor_hit ? "every match moves you" : `raw ${fmt(r.raw)}`} tone={r.cap_hit ? "text-amber-300" : undefined} testid={`${testid}-cap`} />
        </div>
      )}
    </div>
  );
}

function Line({ label, value, hint, tone = "text-white", testid }) {
  return (
    <div className="border border-pb-border/60 bg-pb-elevated/40 px-2 py-1.5" data-testid={testid}>
      <div className="uppercase tracking-widest text-zinc-500 text-[8px] font-bold">{label}</div>
      <div className={`font-mono font-black text-sm ${tone}`}>{value}</div>
      {hint && <div className="text-zinc-600 text-[9px] truncate">{hint}</div>}
    </div>
  );
}

export default function PsiReceipts({ limit = 8 }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/player/psi-receipts?limit=${limit}`).then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, [limit]);
  return (
    <div className="bg-pb-surface border border-pb-border mt-6" data-testid="hub-psi-receipts">
      <button data-testid="hub-psi-receipts-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 p-5 text-left hover:bg-pb-elevated/40 transition-colors">
        <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">
          <Receipt className="w-3.5 h-3.5" /> PSI Movement Receipts
          {items?.length > 0 && <span className="text-zinc-500 tracking-widest">({items.length})</span>}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-xs text-zinc-500 mb-3">Exactly why each match moved your PSI — expected win chance, upset value, margin kicker, and whether the ±cap kicked in.</p>
          {items === null ? <div className="text-sm text-zinc-600 animate-pulse">Loading receipts…</div>
            : items.length === 0 ? <div className="text-sm text-zinc-600" data-testid="psi-receipts-empty">No match movement yet — your first tournament or league result prints a receipt here.</div>
            : <div className="space-y-2" data-testid="psi-receipts-list">{items.map((ev, i) => <PsiReceiptCard key={ev.ref} ev={ev} testid={`psi-receipt-${i}`} />)}</div>}
        </div>
      )}
    </div>
  );
}
